/**
 * The Designs-tier attach mode ("By set" vs "By colour") as a PERSISTED
 * preference (BLU-838 V1 / S13).
 *
 * The mode used to be per-row `useState`, so an operator working a batch in
 * colour mode was thrown back to set mode by any reload — mid-batch, with no
 * warning, and the two modes do very different things (set mode STAGES for the
 * next sync run; colour mode PUBLISHES straight to Shopify variants). Losing
 * the choice silently is how someone stages a design they meant to publish.
 *
 * It lives here rather than inline in `DesignsTier` so the preference has one
 * owner and one storage key: every expanded row reads the same value, and there
 * is a single place to look when the key ever has to change.
 *
 * Degradation is deliberately one-directional: a missing, corrupt, or
 * unreadable value resolves to `'set'`, never `'colour'`. Private-mode browsers
 * and storage-disabled environments therefore land on the mode that changes
 * nothing in Shopify.
 */
import { useCallback, useState } from 'react';

export type AttachMode = 'set' | 'colour';

export const ATTACH_MODE_STORAGE_KEY = 'productSync.designAttachMode';

export function readAttachMode(): AttachMode {
  try {
    return window.localStorage.getItem(ATTACH_MODE_STORAGE_KEY) === 'colour' ? 'colour' : 'set';
  } catch {
    return 'set';
  }
}

export function writeAttachMode(mode: AttachMode): void {
  try {
    window.localStorage.setItem(ATTACH_MODE_STORAGE_KEY, mode);
  } catch {
    /* storage unavailable — the preference is a convenience, never a dependency */
  }
}

export function useAttachModePreference(): [AttachMode, (next: AttachMode) => void] {
  const [mode, setMode] = useState<AttachMode>(readAttachMode);
  const change = useCallback((next: AttachMode) => {
    setMode(next);
    writeAttachMode(next);
  }, []);
  return [mode, change];
}
